import React, { useState, useEffect } from 'react';
import { getCustomers, updateCustomer } from '../api/tailorApi'; // Ensure updateCustomer is imported
import Layout from '../components/Layout';

// --- NEW COMPONENT: CustomerEditCard (for editing customer details) ---
const CustomerEditCard = ({ customer, onSave, onCancel }) => {
    // Initialize form data with customer details
    const [formData, setFormData] = useState({
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        address: customer.address,
        // Add other fields you might want to edit
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSaving(true);
        try {
            await onSave(formData);
            // If onSave completes without error, it handles the UI switch back to list
        } catch (err) {
            // Catch the error re-thrown by handleSaveCustomer in the parent
            console.error("Error saving customer:", err);
            setError(err.message || "Failed to save changes due to a server error.");
        } finally {
            // Only set isSaving to false if an error occurred (success case navigates away)
            if (error) {
                setIsSaving(false);
            }
        }
    };

    const inputClasses = "w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500";
    const labelClasses = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div className="bg-white p-8 shadow-2xl rounded-xl border border-gray-200 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-2">Edit Customer: {customer.custId}</h2>
            {error && <div className="p-3 mb-4 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className={labelClasses}>Full Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className={inputClasses} />
                </div>
                <div>
                    <label className={labelClasses}>Phone Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className={inputClasses} />
                </div>
                <div>
                    <label className={labelClasses}>Address</label>
                    <textarea name="address" value={formData.address} onChange={handleChange} rows="3" className={inputClasses} />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <button type="button" onClick={onCancel} disabled={isSaving} className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-md text-sm hover:bg-gray-100 disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="submit" disabled={isSaving} className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-4 rounded-md text-sm disabled:opacity-50">
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};


// --- NEW COMPONENT: CustomerDetailsCard (for viewing full details) ---
const CustomerDetailsCard = ({ customer, onBack }) => {
    return (
        <div className="bg-white p-8 shadow-2xl rounded-xl border border-gray-200 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 border-b pb-2">Customer Details: {customer.name}</h2>

            <div className="space-y-4">
                <p><span className="font-semibold text-gray-800">Customer ID:</span> {customer.custId}</p>
                <p><span className="font-semibold text-gray-800">Phone:</span> {customer.phone}</p>
                <p><span className="font-semibold text-gray-800">Email:</span> {customer.email}</p>
                <p><span className="font-semibold text-gray-800">Address:</span> {customer.address}</p>
                <p><span className="font-semibold text-gray-800">Member Since:</span> {customer.memberSince}</p>
            </div>

            <div className="mt-6 border-t pt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Order Statistics</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-semibold">Total Orders</span><p className="text-lg font-bold text-gray-900">{customer.totalOrders}</p></div>
                    <div><span className="font-semibold">Pending</span><p className="text-lg font-bold text-red-500">{customer.pendingOrders}</p></div>
                    <div><span className="font-semibold">Completed</span><p className="text-lg font-bold text-green-500">{customer.completedOrders}</p></div>
                    <div><span className="font-semibold">Total Spent</span><p className="text-lg font-bold text-gray-900">₹{customer.totalSpent.toLocaleString('en-IN')}</p></div>
                </div>
            </div>

            <div className="flex justify-end mt-6">
                <button onClick={onBack} className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md text-sm">
                    Back to List
                </button>
            </div>
        </div>
    );
};


/**
 * Augments the basic API customer data with required UI fields (like statistics)
 * which are not provided by the simple get_customers.php endpoint.
 */
const augmentCustomerData = (apiData) => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
        console.warn("No customer data available from API.");
        return [];
    }

    return apiData.map(customer => {
        // Mocked/Generated statistics for UI consistency
        const totalOrders = Math.floor(Math.random() * 15) + 3; // 3 to 17 orders
        const pendingOrders = Math.floor(Math.random() * 3); // 0 to 2 pending
        const completedOrders = totalOrders - pendingOrders;
        const totalSpent = Math.floor(Math.random() * 50000) + 10000; // 10,000 to 60,000 INR
        const memberSince = new Date(Date.now() - Math.floor(Math.random() * 365 * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10); // Random date in last year

        return {
            id: customer.id, // Keep the numeric ID for the API update
            name: customer.name || 'N/A',
            phone: customer.phone || 'N/A',
            address: customer.address || 'Address Not Provided',

            // Generated/Mocked Data
            custId: customer.customer_id || `CUST-${customer.id}`,
            email: customer.email || `${customer.name?.toLowerCase().replace(/\s/g, '.') || 'user'}@email.com`,
            totalOrders: totalOrders,
            pendingOrders: pendingOrders,
            completedOrders: completedOrders,
            totalSpent: totalSpent,
            memberSince: memberSince,
        };
    });
};

// --- CustomerCard (Updated Action Buttons) ---
const CustomerCard = ({ customer, onEdit, onViewDetails }) => {
    const getInitials = (name) => {
        const parts = name.split(' ');
        if (parts.length > 1) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name[0].toUpperCase();
    };

    return (
        <div className="bg-white p-6 shadow-md rounded-lg border border-gray-200">
            <div className="flex items-start justify-between">
                <div className="flex items-center">
                    <div className="w-10 h-10 bg-yellow-100 text-yellow-800 rounded-full flex items-center justify-center font-bold text-lg mr-4">
                        {getInitials(customer.name)}
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-gray-900">{customer.name}</p>
                        <p className="text-sm text-gray-500">{customer.custId}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 space-y-1 text-sm text-gray-600 border-b pb-4 mb-4">
                <p>📞 {customer.phone}</p>
                <p>📧 {customer.email}</p>
                <p>📍 {customer.address}</p>
            </div>

            <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1h4a1 1 0 011 1v12a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1h4V3a1 1 0 011-1zm-4 7a1 1 0 000 2h8a1 1 0 100-2H6zm0 4a1 1 0 100 2h4a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
                Order Statistics
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-semibold">Total Orders</span><p className="text-lg font-bold text-gray-900">{customer.totalOrders}</p></div>
                <div><span className="font-semibold">Pending</span><p className="text-lg font-bold text-red-500">{customer.pendingOrders}</p></div>
                <div><span className="font-semibold">Completed</span><p className="text-lg font-bold text-green-500">{customer.completedOrders}</p></div>
                <div><span className="font-semibold">Total Spent</span><p className="text-lg font-bold text-gray-900">₹{customer.totalSpent.toLocaleString('en-IN')}</p></div>
            </div>

            <p className="mt-4 text-xs text-gray-500">Member Since: {customer.memberSince}</p>

            {/* UPDATED Actions */}
            <div className="flex justify-end space-x-3 mt-6">
                <button
                    onClick={() => onEdit(customer)}
                    className="border border-yellow-500 text-yellow-700 font-semibold py-2 px-4 rounded-md text-sm hover:bg-yellow-50"
                >
                    Edit
                </button>
                <button
                    onClick={() => onViewDetails(customer)}
                    className="bg-gray-800 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-md text-sm"
                >
                    View Details
                </button>
            </div>
        </div>
    );
};


const CustomerListing = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [fetchError, setFetchError] = useState(null); // NEW: State for API fetch error

    // NEW STATE for managing single customer view/edit
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [mode, setMode] = useState('list'); // 'list', 'edit', 'view'

    // --- Data Fetching Logic ---
    const fetchCustomers = async () => {
        setLoading(true);
        setFetchError(null); // Clear previous errors on new fetch attempt
        try {
            const apiResponse = await getCustomers();
            const apiData = Array.isArray(apiResponse) ? apiResponse : apiResponse.customers || [];

            if (apiData.length === 0) {
                setFetchError("No customer data received from the server.");
            }

            setCustomers(augmentCustomerData(apiData));
        } catch (error) {
            console.error("Failed to fetch customers:", error);
            setCustomers([]);
            setFetchError(error.message || "Failed to load customer data. Please check your network or server status."); // Set user-facing error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers();
    }, []);

    // --- Handler Functions ---
    const handleEditClick = (customer) => {
        setSelectedCustomer(customer);
        setMode('edit');
    };

    const handleViewDetailsClick = (customer) => {
        setSelectedCustomer(customer);
        setMode('view');
    };

    const handleBackToList = () => {
        setSelectedCustomer(null);
        setMode('list');
        // Optionally refetch data here if changes were saved, but local state update is often enough
    };

    const handleSaveCustomer = async (updatedData) => {
        // Find the original customer object to get the real ID, if not already in updatedData.id
        const originalCustomer = customers.find(c => c.id === updatedData.id);
        if (!originalCustomer) {
            throw new Error("Customer ID not found for update.");
        }

        try {
            // 1. Prepare API payload (only send fields the API needs)
            const apiPayload = {
                id: updatedData.id,
                name: updatedData.name,
                phone: updatedData.phone,
                address: updatedData.address,
                // Include any other editable fields here
            };

            const response = await updateCustomer(apiPayload); // Call the API helper

            if (response.success) {
                // 2. Update the local state for immediate UI refresh
                // Merge updatedData with existing customer data to keep the augmented fields (like stats)
                setCustomers(prevCustomers =>
                    prevCustomers.map(c =>
                        c.id === updatedData.id ? { ...c, ...originalCustomer, ...updatedData } : c
                    )
                );

                // 3. Switch back to the list view
                handleBackToList();
            } else {
                throw new Error(response.message || "Update failed on server.");
            }
        } catch (error) {
            // Re-throw the error for the edit card to display its error message
            throw error;
        }
    };

    const filteredCustomers = customers.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm) ||
        c.custId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // --- Conditional Rendering ---
    let content;
    let title = 'Customer Listing 📋';
    let subtitle = 'View all customers and their order history.';

    if (mode === 'edit' && selectedCustomer) {
        content = (
            <CustomerEditCard
                customer={selectedCustomer}
                onSave={handleSaveCustomer}
                onCancel={handleBackToList}
            />
        );
        title = 'Edit Customer Details ✍️';
        subtitle = `Modifying details for Customer ID: ${selectedCustomer.custId}`;
    } else if (mode === 'view' && selectedCustomer) {
        content = (
            <CustomerDetailsCard
                customer={selectedCustomer}
                onBack={handleBackToList}
            />
        );
        title = 'Customer Profile 👤';
        subtitle = `Detailed information for ${selectedCustomer.name}`;
    } else { // mode === 'list'
        content = (
            <>
                {/* Search Bar */}
                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Search by name, phone, or customer ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-yellow-500 focus:border-yellow-500"
                    />
                </div>

                {/* API Fetch Error Display */}
                {fetchError && (
                    <div className="p-4 mb-4 bg-red-100 text-red-700 rounded-md text-center font-medium border border-red-300">
                        🚨 **Data Loading Error:** {fetchError}
                    </div>
                )}

                {loading && <div className="text-center text-xl p-8 text-gray-600">Loading Customers... 🔄</div>}

                {!loading && filteredCustomers.length > 0 && (
                    <div className="text-md text-gray-700 mb-4">Displaying **{filteredCustomers.length}** customers.</div>
                )}

                {/* Customer Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredCustomers.map(customer => (
                        <CustomerCard
                            key={customer.id}
                            customer={customer}
                            onEdit={handleEditClick}
                            onViewDetails={handleViewDetailsClick}
                        />
                    ))}
                </div>

                {!loading && filteredCustomers.length === 0 && !fetchError && (
                    <div className="text-center text-gray-500 p-8 border-2 border-dashed border-gray-300 rounded-lg mt-8">
                        {searchTerm
                            ? "No customers found matching your search criteria."
                            : "The customer list is empty."
                        }
                    </div>
                )}
            </>
        );
    }


    return (

        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-sm text-gray-500 mb-6"> {subtitle}</p>

            {content}

        </div>

    );
};

export default CustomerListing;